import { useEffect, useRef } from "react";

import { TextArea } from "@/design-system/TextArea";
import { mergeRefs } from "@/helpers/mergeRefs";
import { useShouldSubmitOnEnter } from "@/hooks/useShouldSubmitOnEnter";

type CommentFieldInputProps = {
  autoFocus?: boolean;
  disabled?: boolean;
  value?: string;
  placeholder?: string;
  isEdit?: boolean;
  isSubmitting?: boolean;
  ref?: React.Ref<HTMLTextAreaElement>;
  disableSubmitOnEnter?: boolean;
  onChange: (value: string) => void;
  onSubmit: () => void;
  onFocus?: () => void;
  onBlur?: () => void;
};

export function CommentFieldInput({
  autoFocus,
  value,
  placeholder,
  isEdit,
  isSubmitting,
  disabled,
  ref,
  disableSubmitOnEnter,
  onSubmit,
  onChange,
  onFocus,
  onBlur,
}: CommentFieldInputProps): React.ReactElement {
  const textAreaRef = useRef<HTMLTextAreaElement | null>(null);
  const shouldSubmitOnEnter = useShouldSubmitOnEnter();

  useEffect(() => {
    if (isEdit && textAreaRef.current) {
      textAreaRef.current.focus();
      textAreaRef.current.setSelectionRange(textAreaRef.current.value.length, textAreaRef.current.value.length);
      textAreaRef.current.scrollTop = textAreaRef.current.scrollHeight;
    }
  }, [isEdit, textAreaRef]);

  const handlePressEnterKey = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === "Enter" && !event.shiftKey && shouldSubmitOnEnter && !disableSubmitOnEnter) {
      event.preventDefault();

      if (!isSubmitting) {
        onSubmit();
      }
    }
  };

  return (
    <TextArea
      appearance="bare"
      height="responsiveCompact"
      pending={isSubmitting}
      disabled={disabled}
      autoFocus={autoFocus && shouldSubmitOnEnter}
      enterKeyHint={shouldSubmitOnEnter ? "send" : "enter"}
      data-testid="comment-field"
      name="content"
      ref={mergeRefs(textAreaRef, ref)}
      value={value || ""}
      onChange={(event) => onChange(event.target.value)}
      placeholder={placeholder}
      {...{ onFocus, onBlur }}
      // on desktop: shift+Enter inserts a newline, plain Enter submits;
      // on touch: Enter always inserts a newline
      onKeyDown={handlePressEnterKey}
    />
  );
}
